import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { History, Loader2 } from 'lucide-react';
import {
  getTaskSignatures,
  listTaskSignatureBypasses,
} from '../../services/api';
import type {
  TaskSignatureBypassResponse,
  TaskSignatureResponse,
} from '../../services/api/types.gen';
import { SignatureCard } from './SignatureCard';
import { BypassCard } from './BypassCard';

/**
 * One chart's signing history is two record types, not one.
 *
 * A forfeiture is a signing event — it is what completed the chart — so a
 * dialog that lists only `task_signatures` tells a bypassed chart's story with
 * the decisive moment missing, while the PDF, the CMS-485, the badge tooltip
 * and the visits report all report it. They are interleaved by timestamp
 * because the reader is reconstructing a sequence, not browsing two tables.
 */
type HistoryEntry =
  | { kind: 'signature'; at: string; signature: TaskSignatureResponse }
  | { kind: 'bypass'; at: string; bypass: TaskSignatureBypassResponse };

interface SignatureHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  /**
   * Read the history of a soft-deleted task. The signatures themselves are
   * never soft-deleted, so this only gets past the route's existence check on
   * the task; the rows come back the same either way. The bypass route checks
   * no such existence and bypasses are not soft-deleted either, so it needs
   * nothing here and reads a deleted task's bypasses as it reads a live one's.
   */
  includeDeleted?: boolean;
}

export const SignatureHistoryDialog: React.FC<SignatureHistoryDialogProps> = ({
  open,
  onOpenChange,
  taskId,
  includeDeleted = false,
}) => {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getTaskSignatures({
        path: { task_id: taskId },
        query: {
          include_invalidated: true,
          ...(includeDeleted && { include_deleted: true }),
        },
      }),
      listTaskSignatureBypasses({ path: { task_id: taskId } }),
    ])
      .then(([sigRes, bypassRes]) => {
        if (cancelled) return;
        // The generated client does not throw on 4xx/5xx. A history that
        // silently drops forfeitures is the bug this dialog is being fixed
        // for, so a failed bypass fetch is surfaced rather than rendered as
        // "no bypasses".
        if (sigRes.error || bypassRes.error) {
          console.error(
            'Failed to load signature history:',
            sigRes.error ?? bypassRes.error
          );
          setError('Unable to load signature history. Please try again.');
          return;
        }
        const merged: HistoryEntry[] = [
          ...(sigRes.data ?? []).map(
            (signature): HistoryEntry => ({
              kind: 'signature',
              at: signature.signed_at,
              signature,
            })
          ),
          ...(bypassRes.data ?? []).map(
            (bypass): HistoryEntry => ({
              kind: 'bypass',
              at: bypass.bypassed_at,
              bypass,
            })
          ),
        ];
        setEntries(merged);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load signature history:', err);
        setError('Unable to load signature history. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, taskId, includeDeleted]);

  const sorted = (entries ?? [])
    .slice()
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <History className='h-5 w-5 text-primary' />
            Signature history
          </DialogTitle>
          <DialogDescription>
            Every signature, invalidation, and bypass on this chart, newest
            first.
          </DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-y-auto pr-1'>
          {loading ? (
            <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Loading...
            </div>
          ) : error ? (
            <div className='py-6 text-sm text-destructive'>{error}</div>
          ) : sorted.length === 0 ? (
            <div className='py-6 text-sm text-muted-foreground'>
              Nothing has been signed on this chart yet.
            </div>
          ) : (
            <div className='space-y-2'>
              {sorted.map(entry =>
                entry.kind === 'bypass' ? (
                  <BypassCard
                    key={`bypass-${entry.bypass.id}`}
                    bypass={entry.bypass}
                  />
                ) : (
                  <SignatureCard
                    key={`signature-${entry.signature.id}`}
                    signature={entry.signature}
                    showRecordedAt
                  />
                )
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
